/* eslint-disable react/no-unstable-nested-components */
import { React, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './index.scss';

import axios from 'axios';
import moment from 'moment';
import { ToastContainer, toast } from 'react-toastify';
import { axiosWrapper, formatDateDataPicker } from 'utils/Helper';
import { URLS } from 'routes/Urls';

import { Table, TableHeader, TableRow } from 'component/Table';
import { Tab } from 'component/Tab';
import { Loader } from 'component/Loader';

import { ReactComponent as ResetIcon } from 'assets/images/icons/update2.svg';
import { CreateReportDialog, DeleteReportDialog } from 'component/Dialog';
import { DatePickerWrapper } from 'component/DataPicker';
import { useUser } from 'routes/UserContext';
import { ReactComponent as EditIcon } from '../../assets/images/icons/mode_edit_24px.svg';
import { ReactComponent as CommentIcon } from '../../assets/images/icons/chat.svg';
import { ReactComponent as ViewIcon } from '../../assets/images/icons/Eye-on.svg';
import { ReactComponent as SortByIcon } from '../../assets/images/icons/Sort-by2.svg';
import { FilterByStatusDialog } from './Filters/FilterReportList';
import ReportsList from './ReportsList';
import { NTS_HEADERS, HA_HEADERS } from '../../consts/ReportHeaders';
import 'assets/styles/pages/eore.scss';
import { getValidationStatuses } from 'api/FilterApi';
import { AsyncPaginate } from 'react-select-async-paginate';

const ImsmaIdFilterDropdown = ({ title, selectedValues, onSelectionChange, onClose, position }) => {
  const dropdownRef = useRef(null);
  const [tempValue, setTempValue] = useState(selectedValues?.[0]?.value || '');

  const handleApply = () => {
    const values = tempValue.trim() ? [{ value: tempValue.trim(), label: tempValue.trim() }] : [];
    onSelectionChange(values);
    onClose();
  };

  const handleCancel = () => {
    setTempValue(selectedValues?.[0]?.value || '');
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      ref={dropdownRef}
      className="filter-dropdown"
      style={{
        top: `${position?.top || 0}px`,
        left: `${position?.left || 0}px`,
        width: '280px',
      }}
    >
      <div className="filter-dropdown-header">
        <h4>Filter by {title}</h4>
      </div>
      <div className="filter-dropdown-content">
        <div
          className="filter-select-wrapper"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            placeholder="Enter IMSMA ID..."
            className="filter-text-input"
            style={{
              width: '250px',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
      </div>
      <div className="filter-dropdown-actions">
        <button onClick={handleCancel} className="cancel-button">
          Cancel
        </button>
        <button onClick={handleApply} className="apply-button">
          Apply
        </button>
      </div>
    </div>
  );
};

const FilterDropdown = ({
  title,
  options,
  selectedValues,
  onSelectionChange,
  onClose,
  position,
  isLoading,
  reportType,
  filterType,
  currentFilters,
  currentDates,
}) => {
  const dropdownRef = useRef(null);
  const [tempSelectedValues, setTempSelectedValues] = useState(selectedValues || []);

  const loadOptions = async (searchQuery, loadedOptions, { page }) => {
    try {
      if (filterType === 'validation_status') {
        try {
          const query = {};

          if (currentDates?.startDate) query.date_from = currentDates.startDate;
          if (currentDates?.endDate) query.date_to = currentDates.endDate;

          if (currentFilters?.selectedDonor?.length > 0) {
            query.donor = currentFilters?.selectedDonor?.map((d) => d.value || d);
          }
          if (currentFilters?.selectedCustomer?.length > 0) {
            query['task-customer'] = currentFilters?.selectedCustomer?.map((c) => c.value || c);
          }
          if (currentFilters?.selectedTeam?.length > 0) {
            query.team_id = currentFilters?.selectedTeam?.map((t) => t.value || t);
          }
          if (currentFilters?.selectedLocalCommunity?.length > 0) {
            query.local_community = currentFilters?.selectedLocalCommunity?.map(
              (c) => c.value || c
            );
          }
          if (currentFilters?.selectedPolygonName?.length > 0) {
            query.hazard_name = currentFilters?.selectedPolygonName?.map((p) => p.value || p);
          }

          if (reportType === 'ha') {
            if (currentFilters?.selectedAreaType?.length > 0) {
              query.hazard_area_type = currentFilters?.selectedAreaType?.map((a) => a.value || a);
            }
            if (currentFilters?.selectedHaMfBacType?.length > 0) {
              query.hazard_type = currentFilters?.selectedHaMfBacType?.map((h) => h.value || h);
            }
          }

          if (reportType === 'nts') {
            if (currentFilters?.selectedType?.length > 0) {
              query.type = currentFilters?.selectedType?.map((t) => t.value);
            }
          }

          const response = await getValidationStatuses(reportType, query);

          let options = [];
          if (Array.isArray(response.data) && response.data.length > 0) {
            const validItems = response.data.filter((item) => {
              try {
                const value = item?.value ?? item;
                const label = item?.label ?? item;

                const isValidValue =
                  value != null && typeof value === 'string' && value.trim() !== '';
                const isValidLabel =
                  label != null && typeof label === 'string' && label.trim() !== '';
                return isValidValue && isValidLabel;
              } catch (error) {
                console.error(`Error processing validation status`, error);
                return false;
              }
            });

            options =
              validItems.length > 0
                ? validItems.map((item) => ({
                    value: item?.value || item,
                    label: item?.label || item,
                  }))
                : [{ value: 'no-data', label: 'Немає даних', isDisabled: true }];
          } else {
            options = [{ value: 'no-data', label: 'Немає даних', isDisabled: true }];
          }

          return {
            options,
            hasMore: false,
            additional: { page: 1 },
          };
        } catch (error) {
          console.error(`Error loading validation statuses:`, error);
          return {
            options: [{ value: 'no-data', label: 'Немає даних', isDisabled: true }],
            hasMore: false,
            additional: { page: 1 },
          };
        }
      }

      if (filterType === 'nts_type') {
        try {
          const response = await axios.get('/reports/nts/choices/type/');
          const data = response.data;
          const rawList = Array.isArray(data) ? data : (data?.results ?? []);
          let options = [];

          if (rawList.length > 0) {
            options = rawList.map((item) => ({
              value: item?.value ?? item,
              label: item?.label ?? item,
            }));
          } else {
            options = [{ value: 'no-data', label: 'Немає даних', isDisabled: true }];
          }

          return { options, hasMore: false, additional: { page: 1 } };
        } catch (error) {
          console.error('Error loading nts type choices:', error);
          return {
            options: [{ value: 'no-data', label: 'Немає даних', isDisabled: true }],
            hasMore: false,
            additional: { page: 1 },
          };
        }
      }

      if (filterType === 'imsma_id') {
        return {
          options: [],
          hasMore: false,
          additional: { page: 1 },
        };
      }

      let endpoint = '';
      if (reportType === 'nts') {
        switch (filterType) {
          case 'team_id':
            endpoint = '/reports/nts/choices/team-id/';
            break;
          case 'local_community':
            endpoint = '/reports/nts/choices/local-community/';
            break;
          case 'donor':
            endpoint = '/reports/nts/choices/donor/';
            break;
          case 'customer':
            endpoint = '/reports/nts/choices/task-customer/';
            break;
          case 'settlement':
            endpoint = '/reports/nts/choices/settlement/';
            break;
          case 'district':
            endpoint = '/reports/nts/choices/district/';
            break;
          case 'region':
            endpoint = '/reports/nts/choices/region/';
            break;
          case 'hazard_name':
            endpoint = '/reports/nts/choices/hazard-name/';
            break;
          default:
            endpoint = `/reports/nts/filters/choices/?choice_name=${filterType}`;
        }
      } else if (reportType === 'ha') {
        switch (filterType) {
          case 'team_id':
            endpoint = '/reports/ha/choices/team-id/';
            break;
          case 'local_community':
            endpoint = '/reports/ha/choices/local-community/';
            break;
          case 'donor':
            endpoint = '/reports/ha/choices/donor/';
            break;
          case 'customer':
            endpoint = '/reports/ha/choices/task-customer/';
            break;
          case 'settlement':
            endpoint = '/reports/ha/choices/settlement/';
            break;
          case 'district':
            endpoint = '/reports/ha/choices/district/';
            break;
          case 'region':
            endpoint = '/reports/ha/choices/region/';
            break;
          case 'hazard_name':
            endpoint = '/reports/ha/choices/hazard-name/';
            break;
          case 'ha_area_type':
            endpoint = '/reports/ha/choices/hazard-area-type/';
            break;
          case 'ha_mf_bac_type':
            endpoint = '/reports/ha/choices/hazard-type/';
            break;
          default:
            endpoint = `/reports/ha/filters/choices/?choice_name=${filterType}`;
        }
      }

      const params = new URLSearchParams();
      params.append('page', page);

      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      if (currentDates?.startDate) {
        params.append('date_from', currentDates?.startDate);
      }
      if (currentDates?.endDate) {
        params.append('date_to', currentDates?.endDate);
      }

      if (currentFilters?.selectedDonor?.length > 0 && filterType !== 'donor') {
        currentFilters?.selectedDonor?.forEach((donor) => {
          params.append('donor', donor?.value || donor);
        });
      }
      if (currentFilters?.selectedCustomer?.length > 0 && filterType !== 'customer') {
        currentFilters?.selectedCustomer?.forEach((customer) => {
          params.append('task-customer', customer?.value || customer);
        });
      }
      if (currentFilters?.selectedTeam?.length > 0 && filterType !== 'team_id') {
        currentFilters?.selectedTeam?.forEach((team) => {
          params.append('team_id', team?.value || team);
        });
      }
      if (currentFilters?.selectedLocalCommunity?.length > 0 && filterType !== 'local_community') {
        currentFilters?.selectedLocalCommunity?.forEach((community) => {
          params.append('local_community', community?.value || community);
        });
      }
      if (currentFilters?.selectedPolygonName?.length > 0 && filterType !== 'hazard_name') {
        currentFilters?.selectedPolygonName?.forEach((polygon) => {
          params.append('hazard_name', polygon?.value || polygon);
        });
      }
      if (currentFilters?.selectedStatus?.length > 0 && filterType !== 'validation_status') {
        currentFilters?.selectedStatus?.forEach((status) => {
          params.append('validation_status', status?.value || status);
        });
      }

      if (reportType === 'ha') {
        if (currentFilters?.selectedAreaType?.length > 0 && filterType !== 'ha_area_type') {
          currentFilters?.selectedAreaType?.forEach((areaType) => {
            params.append('hazard_area_type', areaType?.value || areaType);
          });
        }
        if (currentFilters?.selectedHaMfBacType?.length > 0 && filterType !== 'ha_mf_bac_type') {
          currentFilters?.selectedHaMfBacType?.forEach((type) => {
            params.append('hazard_type', type?.value || type);
          });
        }
      }

      if (reportType === 'nts') {
        if (currentFilters?.selectedType?.length > 0 && filterType !== 'nts_type') {
          currentFilters?.selectedType?.forEach((type) => {
            params.append('type', type?.value);
          });
        }
      }

      const response = await axios.get(`${endpoint}?${params.toString()}`);

      let options = [];
      let hasMore = false;

      if (Array.isArray(response.data)) {
        options = response?.data?.map((item) => ({
          value: item,
          label: item,
        }));
        hasMore = false;
      } else if (response.data && response.data.results) {
        options = response?.data?.results?.map((item) => ({
          value: item,
          label: item,
        }));
        hasMore = Boolean(response?.data?.next);
      } else {
        options = [
          {
            value: 'no-data',
            label: 'Немає даних',
            isDisabled: true,
          },
        ];
        hasMore = false;
      }

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    } catch (error) {
      console.error(`Error loading options:`, error);
      return {
        options: [
          {
            value: 'no-data',
            label: 'Немає даних',
            isDisabled: true,
          },
        ],
        hasMore: false,
        additional: { page: 1 },
      };
    }
  };

  const handleSelectChange = (selectedOptions) => {
    const validOptions = selectedOptions
      ? selectedOptions.filter((option) => !option.isDisabled)
      : [];
    setTempSelectedValues(validOptions);
  };

  const handleApply = () => {
    onSelectionChange(tempSelectedValues);
    onClose();
  };

  const handleCancel = () => {
    setTempSelectedValues(selectedValues || []);
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      ref={dropdownRef}
      className="filter-dropdown"
      style={{
        top: `${position?.top || 0}px`,
        left: `${position?.left || 0}px`,
      }}
    >
      <div className="filter-dropdown-header">
        <h4>Filter by {title}</h4>
      </div>
      <div className="filter-dropdown-content">
        <div
          className="filter-select-wrapper"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <AsyncPaginate
            isMulti
            isSearchable={
              !(
                filterType === 'nts_type' ||
                filterType === 'validation_status' ||
                filterType === 'ha_area_type' ||
                filterType === 'ha_mf_bac_type'
              )
            }
            value={tempSelectedValues}
            loadOptions={loadOptions}
            onChange={handleSelectChange}
            additional={{ page: 1 }}
            placeholder={`Select ${title?.toLowerCase()}...`}
            className="filter-select"
            classNamePrefix="filter-select"
            cacheOptions={false}
            defaultOptions={true}
            isOptionDisabled={(option) => option?.isDisabled}
            noOptionsMessage={({ inputValue }) =>
              inputValue ? 'Nothing found' : 'Enter text to search'
            }
            styles={{
              menu: (provided) => ({
                ...provided,
                maxHeight: 200,
              }),
              menuList: (provided) => ({
                ...provided,
                maxHeight: 200,
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state?.isDisabled ? 'transparent' : provided?.backgroundColor,
                color: state?.isDisabled ? '#999' : provided?.color,
                cursor: state?.isDisabled ? 'default' : 'pointer',
                ':hover': {
                  backgroundColor: state?.isDisabled
                    ? 'transparent'
                    : provided[':hover']?.backgroundColor,
                },
              }),
            }}
          />
        </div>
        <div className="filter-dropdown-actions">
          <button onClick={handleCancel} className="cancel-button">
            Cancel
          </button>
          <button onClick={handleApply} className="apply-button">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export const NTS_HA_REPORT = () => {
  const { user } = useUser();
  const location = useLocation();
  const [ntsData, setNtsData] = useState([]);
  const [haData, setHaData] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [ntsLoader, setNtsLoader] = useState(false);
  const [haLoader, setHaLoader] = useState(false);
  const [resetFiltersStatus, setResetFiltersStatus] = useState(false);
  const [isNtsNextProperty, setIsNtsNextProperty] = useState('');
  const [isHaNextProperty, setIsHaNextProperty] = useState('');
  const [ntsDataLoaded, setNtsDataLoaded] = useState(false);
  const [haDataLoaded, setHaDataLoaded] = useState(false);
  const isInitialRender = useRef(true);

  const [ntsRotations, setNtsRotations] = useState({
    report_date: 0,
  });
  const [haRotations, setHaRotations] = useState({
    report_date: 0,
    area: 0,
  });
  const [ntsLastClickedIcon, setNtsLastClickedIcon] = useState(null);
  const [haLastClickedIcon, setHaLastClickedIcon] = useState(null);
  const teamLeadId = location.state?.teamLeadId || null;
  const [ntsStartDate, setNtsStartDate] = useState(() =>
    formatDateDataPicker(location?.state?.startDate)
  );
  const [ntsEndDate, setNtsEndDate] = useState(() =>
    formatDateDataPicker(location?.state?.endDate)
  );
  const [haStartDate, setHaStartDate] = useState(() =>
    formatDateDataPicker(location?.state?.startDate)
  );
  const [haEndDate, setHaEndDate] = useState(() => formatDateDataPicker(location?.state?.endDate));

  const [ntsFilters, setNtsFilters] = useState({
    selectedFilters: [],
    selectedStatus: [],
    selectedDonor: [],
    selectedCustomer: [],
    selectedTeam: [],
    selectedLocalCommunity: [],
    selectedPolygonName: [],
    selectedType: [],
    selectedImsmaId: [],
  });

  const [haFilters, setHaFilters] = useState({
    selectedFilters: [],
    selectedStatus: [],
    selectedDonor: [],
    selectedCustomer: [],
    selectedTeam: [],
    selectedLocalCommunity: [],
    selectedPolygonName: [],
    selectedType: [],
    selectedImsmaId: [],
  });

  const [activeFilter, setActiveFilter] = useState(null);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });

  const [ntsHeaderFilters, setNtsHeaderFilters] = useState({
    team_id: [],
    local_community: [],
    hazard_name: [],
    nts_type: [],
    imsma_id: [],
    validation_status: [],
  });

  const [haHeaderFilters, setHaHeaderFilters] = useState({
    team_id: [],
    local_community: [],
    hazard_name: [],
    ha_area_type: [],
    ha_mf_bac_type: [],
    imsma_id: [],
    validation_status: [],
  });

  const formatDate = (dateString) => {
    if (!dateString) {
      return '-';
    }
    const date = moment(dateString, 'DD.MM.YYYY');
    if (!date.isValid()) {
      return '-';
    }
    return `${date.format('L')}`;
  };

  const isNtsFilterableHeader = (key) => {
    return [
      'team_id',
      'local_community',
      'hazard_name',
      'nts_type',
      'imsma_id',
      'validation_status',
    ].includes(key);
  };

  const isHaFilterableHeader = (key) => {
    return [
      'team_id',
      'local_community',
      'hazard_name',
      'ha_area_type',
      'ha_mf_bac_type',
      'imsma_id',
      'validation_status',
    ].includes(key);
  };

  const isFilterableHeader = (key) => {
    return activeTabIndex === 0 ? isNtsFilterableHeader(key) : isHaFilterableHeader(key);
  };

  const getCurrentHeaderFilters = () => {
    return activeTabIndex === 0 ? ntsHeaderFilters : haHeaderFilters;
  };

  const getActiveFiltersCount = (headerKey) => {
    const filters = getCurrentHeaderFilters();
    return filters[headerKey]?.length || 0;
  };

  const getCurrentCompleteFilters = () => {
    const mainFilters = activeTabIndex === 0 ? ntsFilters : haFilters;
    const headerFilters = getCurrentHeaderFilters();

    if (activeTabIndex === 0) {
      return {
        ...mainFilters,
        selectedTeam: headerFilters?.team_id || mainFilters?.selectedTeam || [],
        selectedLocalCommunity:
          headerFilters?.local_community || mainFilters?.selectedLocalCommunity || [],
        selectedPolygonName: headerFilters?.hazard_name || mainFilters?.selectedPolygonName || [],
        selectedType: headerFilters?.nts_type || mainFilters?.selectedType || [],
        selectedImsmaId: headerFilters?.imsma_id || mainFilters?.selectedImsmaId || [],
        selectedStatus: headerFilters?.validation_status || mainFilters?.selectedStatus || [],
      };
    } else {
      return {
        ...mainFilters,
        selectedTeam: headerFilters?.team_id || mainFilters?.selectedTeam || [],
        selectedLocalCommunity:
          headerFilters?.local_community || mainFilters?.selectedLocalCommunity || [],
        selectedPolygonName: headerFilters?.hazard_name || mainFilters?.selectedPolygonName || [],
        selectedAreaType: headerFilters?.ha_area_type || mainFilters?.selectedAreaType || [],
        selectedHaMfBacType:
          headerFilters?.ha_mf_bac_type || mainFilters?.selectedHaMfBacType || [],
        selectedImsmaId: headerFilters?.imsma_id || mainFilters?.selectedImsmaId || [],
        selectedStatus: headerFilters?.validation_status || mainFilters?.selectedStatus || [],
      };
    }
  };

  const handleHeaderClick = async (event, headerKey) => {
    if (!isFilterableHeader(headerKey)) {
      return;
    }

    event.stopPropagation();

    let headerElement = event.target;
    while (headerElement && headerElement.tagName !== 'TH') {
      headerElement = headerElement.parentElement;
    }

    if (!headerElement) {
      return;
    }

    let tableContainer = headerElement;
    while (tableContainer && !tableContainer.classList.contains('nts-table')) {
      tableContainer = tableContainer.parentElement;
    }

    if (!tableContainer) {
      tableContainer = headerElement.closest('.nts') || document.body;
    }

    const headerRect = headerElement.getBoundingClientRect();
    const containerRect = tableContainer.getBoundingClientRect();

    const dropdownWidth = 320;
    const dropdownHeight = 300;
    const margin = 10;

    let left = headerRect.left - containerRect.left;
    let top = headerRect.bottom - containerRect.top + 4;

    if (left + dropdownWidth > containerRect.width - margin) {
      left = containerRect.width - dropdownWidth - margin;
    }
    if (left < margin) {
      left = margin;
    }

    if (headerRect.bottom + dropdownHeight > window.innerHeight - margin) {
      top = headerRect.top - containerRect.top - dropdownHeight - 4;
      if (top < margin) {
        top = margin;
      }
    }

    setFilterPosition({
      top: top,
      left: left,
    });

    if (activeFilter === headerKey) {
      setActiveFilter(null);
      return;
    }
    setActiveFilter(headerKey);
  };

  const handleHeaderFilterChange = (headerKey, selectedValues) => {
    if (activeTabIndex === 0) {
      setNtsHeaderFilters((prev) => ({
        ...prev,
        [headerKey]: selectedValues,
      }));
    } else {
      setHaHeaderFilters((prev) => ({
        ...prev,
        [headerKey]: selectedValues,
      }));
    }

    const currentFilters =
      activeTabIndex === 0
        ? { ...ntsHeaderFilters, [headerKey]: selectedValues }
        : { ...haHeaderFilters, [headerKey]: selectedValues };

    const currentMainFilters = activeTabIndex === 0 ? ntsFilters : haFilters;

    let mappedFilters = {};

    if (activeTabIndex === 0) {
      mappedFilters = {
        ...currentMainFilters,
        selectedTeam: currentFilters.team_id || [],
        selectedLocalCommunity: currentFilters.local_community || [],
        selectedPolygonName: currentFilters.hazard_name || [],
        selectedType: currentFilters.nts_type || [],
        selectedImsmaId: currentFilters.imsma_id || [],
        selectedStatus: currentFilters.validation_status || [],
      };
    } else {
      mappedFilters = {
        ...currentMainFilters,
        selectedTeam: currentFilters.team_id || [],
        selectedLocalCommunity: currentFilters.local_community || [],
        selectedPolygonName: currentFilters.hazard_name || [],
        selectedAreaType: currentFilters.ha_area_type || [],
        selectedHaMfBacType: currentFilters.ha_mf_bac_type || [],
        selectedImsmaId: currentFilters.imsma_id || [],
        selectedStatus: currentFilters.validation_status || [],
      };
    }

    handleNtsHaFiltersChange(mappedFilters);
  };

  const closeFilter = () => {
    setActiveFilter(null);
  };

  const clearAllHeaderFilters = () => {
    if (activeTabIndex === 0) {
      setNtsHeaderFilters({
        team_id: [],
        local_community: [],
        hazard_name: [],
        nts_type: [],
        imsma_id: [],
        validation_status: [],
      });
    } else {
      setHaHeaderFilters({
        team_id: [],
        local_community: [],
        hazard_name: [],
        ha_area_type: [],
        ha_mf_bac_type: [],
        imsma_id: [],
        validation_status: [],
      });
    }
  };

  const ntsInitParams = {
    validation_status: [],
    hazard_area_type: [],
    dateFrom: ntsStartDate,
    dateTo: ntsEndDate,
    page: 1,
    ordering: '',
    search: '',
    donor: [],
    'task-customer': [],
    team_id: [],
    local_community: [],
    hazard_name: [],
    imsma_id: [],
    type: [],
  };

  const haInitParams = {
    validation_status: [],
    hazard_area_type: [],
    dateFrom: haStartDate,
    dateTo: haEndDate,
    page: 1,
    ordering: '',
    search: '',
    donor: [],
    'task-customer': [],
    team_id: [],
    local_community: [],
    hazard_name: [],
    hazard_type: [],
    square_min: '',
    square_max: '',
    imsma_id: [],
  };

  const [ntsParams, setNtsParams] = useState(ntsInitParams);
  const [haParams, setHaParams] = useState(haInitParams);
  const [sortDirectionNts, setSortDirectionNts] = useState(true);
  const [sortDirectionHa, setSortDirectionHa] = useState(true);

  const ntsAbortControllerRef = useRef(null);
  const haAbortControllerRef = useRef(null);

  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  const generateUrl = (params, reportType) => {
    let url = `/reports/${reportType}`;

    const queryParams = [];

    Object.keys(params).forEach((key) => {
      if (params[key]) {
        if (
          key === 'ordering' ||
          key === 'page' ||
          key === 'search' ||
          key === 'dateFrom' ||
          key === 'dateTo'
        ) {
          return;
        }

        if (Array.isArray(params[key]) && params[key].length > 0) {
          queryParams.push(params[key].map((value) => `${key}=${value}`).join('&'));
        } else if (params[key] instanceof Date) {
          queryParams.push(`${key}=${params[key].toISOString()}`);
        } else if (typeof params[key] === 'string' && params[key].trim() !== '') {
          queryParams.push(`${key}=${encodeURIComponent(params[key].trim())}`);
        }
      }
    });

    if (params.page) {
      queryParams.push(`page=${params.page}`);
    }
    if (params.ordering && params.ordering?.trim() !== '') {
      queryParams.push(`ordering=${params.ordering}`);
    }
    if (params.search && params.search?.trim() !== '') {
      queryParams.push(`search=${encodeURIComponent(params.search?.trim())}`);
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return url;
  };

  const fetchDataWithSortingAndFilter = (
    params,
    setData,
    setIsNextProperty,
    reportType,
    dateFrom,
    dateTo,
    teamLeadId,
    abortControllerRef,
    setTabLoader,
    isFirstPage = true
  ) => {
    const newAbortController = new AbortController();
    abortControllerRef.current = newAbortController;

    setTabLoader(true);
    const url = generateUrl(params, reportType);

    axiosWrapper(() => {
      axios
        .get(url, {
          params: {
            date_from: dateFrom || null,
            date_to: dateTo || null,
            user_id: teamLeadId || null,
          },
          signal: newAbortController.signal,
        })
        .then((res) => {
          if (isFirstPage) {
            setData(res?.data?.results);
          } else {
            setData((prev) => [...prev, ...res?.data?.results]);
          }
          setIsNextProperty(res?.data?.next);
        })
        .catch((err) => {
          console.error('Error fetching data', err);
        })
        .finally(() => {
          setTabLoader(false);
        });
    });
  };

  useEffect(() => {
    if (isInitialRender?.current || resetFiltersStatus) {
      return;
    }

    if (activeTabIndex === 0) {
      if (!ntsDataLoaded || ntsParams?.page > 1) {
        if (ntsAbortControllerRef.current) {
          ntsAbortControllerRef.current.abort();
        }

        fetchDataWithSortingAndFilter(
          ntsParams,
          setNtsData,
          setIsNtsNextProperty,
          'nts/',
          ntsParams?.dateFrom,
          ntsParams?.dateTo,
          teamLeadId,
          ntsAbortControllerRef,
          setNtsLoader,
          ntsParams?.page === 1
        );
        setNtsDataLoaded(true);
      }
    }

    return () => {
      if (ntsAbortControllerRef.current) {
        ntsAbortControllerRef.current.abort();
      }
    };
  }, [ntsParams]);

  useEffect(() => {
    if (resetFiltersStatus) {
      return;
    }

    if (activeTabIndex === 1) {
      if (!haDataLoaded || haParams?.page > 1) {
        if (haAbortControllerRef.current) {
          haAbortControllerRef.current.abort();
        }

        fetchDataWithSortingAndFilter(
          haParams,
          setHaData,
          setIsHaNextProperty,
          'ha/',
          haParams?.dateFrom,
          haParams?.dateTo,
          teamLeadId,
          haAbortControllerRef,
          setHaLoader,
          haParams?.page === 1
        );
        setHaDataLoaded(true);
      }
    }

    return () => {
      if (haAbortControllerRef.current) {
        haAbortControllerRef.current.abort();
      }
    };
  }, [haParams]);

  const loadMoreData = (type) => {
    switch (type) {
      case 'nts':
        setNtsLoader(true);
        setNtsParams((prevParams) => ({
          ...prevParams,
          page: prevParams?.page + 1,
        }));
        break;
      case 'ha':
        setHaLoader(true);
        setHaParams((prevParams) => ({
          ...prevParams,
          page: prevParams?.page + 1,
        }));
        break;
      default:
        break;
    }
  };

  const handleSelectedStatusChange = (selectedNtsStatus, selectedHaStatus, selectedAreaType) => {
    if (!Array.isArray(selectedNtsStatus || selectedHaStatus)) {
      return;
    }
    if (activeTabIndex === 0) {
      setResetFiltersStatus(false);
      setNtsData([]);
      setNtsDataLoaded(false);
      setNtsParams((prevParams) => ({
        ...prevParams,
        page: 1,
        validation_status: selectedNtsStatus?.map((status) => status?.value),
      }));
    } else if (activeTabIndex === 1) {
      setResetFiltersStatus(false);
      setHaData([]);
      setHaDataLoaded(false);
      setHaParams((prevParams) => ({
        ...prevParams,
        page: 1,
        hazard_area_type: selectedAreaType?.map((areaType) => areaType?.value),
        validation_status: selectedHaStatus?.map((status) => status?.value),
      }));
    }
  };

  const handleNtsHaFiltersChange = (filters) => {
    setResetFiltersStatus(false);

    if (activeTabIndex === 0) {
      const currentNtsFilters = ntsFilters;
      const currentHeaderFilters = ntsHeaderFilters;

      const mergedFilters = {
        ...currentNtsFilters,
        ...filters,
        selectedTeam: filters?.selectedTeam || currentNtsFilters?.selectedTeam || [],
        selectedLocalCommunity:
          filters?.selectedLocalCommunity || currentNtsFilters?.selectedLocalCommunity || [],
        selectedPolygonName:
          filters?.selectedPolygonName || currentNtsFilters?.selectedPolygonName || [],
        selectedType: filters?.selectedType || currentNtsFilters?.selectedType || [],
        selectedImsmaId: filters?.selectedImsmaId || currentNtsFilters?.selectedImsmaId || [],
        selectedStatus: filters?.selectedStatus || currentNtsFilters?.selectedStatus || [],
        selectedDonor: filters?.selectedDonor || currentNtsFilters?.selectedDonor || [],
        selectedCustomer: filters?.selectedCustomer || currentNtsFilters?.selectedCustomer || [],
      };

      setNtsFilters(mergedFilters);
      setNtsData([]);
      setNtsDataLoaded(false);
      setNtsParams((prevParams) => ({
        ...prevParams,
        page: 1,
        validation_status: mergedFilters.selectedStatus?.map((status) => status.value) || [],
        donor: mergedFilters.selectedDonor?.map((donor) => donor.value) || [],
        'task-customer': mergedFilters.selectedCustomer?.map((c) => c.value) || [],
        team_id: mergedFilters.selectedTeam?.map((team) => team.value) || [],
        local_community:
          mergedFilters.selectedLocalCommunity?.map((community) => community.value) || [],
        hazard_name: mergedFilters.selectedPolygonName?.map((polygon) => polygon.value) || [],
        imsma_id:
          mergedFilters.selectedImsmaId?.length > 0 ? mergedFilters.selectedImsmaId[0].value : '',
        type: mergedFilters.selectedType?.map((type) => type.value) || [],
      }));
    } else if (activeTabIndex === 1) {
      const currentHaFilters = haFilters;
      const currentHeaderFilters = haHeaderFilters;

      const mergedFilters = {
        ...currentHaFilters,
        ...filters,
        selectedTeam: filters?.selectedTeam || currentHaFilters?.selectedTeam || [],
        selectedLocalCommunity:
          filters?.selectedLocalCommunity || currentHaFilters?.selectedLocalCommunity || [],
        selectedPolygonName:
          filters?.selectedPolygonName || currentHaFilters?.selectedPolygonName || [],
        selectedAreaType: filters?.selectedAreaType || currentHaFilters?.selectedAreaType || [],
        selectedHaMfBacType:
          filters?.selectedHaMfBacType || currentHaFilters?.selectedHaMfBacType || [],
        selectedImsmaId: filters?.selectedImsmaId || currentHaFilters?.selectedImsmaId || [],
        selectedStatus: filters?.selectedStatus || currentHaFilters?.selectedStatus || [],
        selectedDonor: filters?.selectedDonor || currentHaFilters?.selectedDonor || [],
        selectedCustomer: filters?.selectedCustomer || currentHaFilters?.selectedCustomer || [],
        selectedSquare: filters?.selectedSquare ||
          currentHaFilters?.selectedSquare || { min: '', max: '' },
      };

      setHaFilters(mergedFilters);
      setHaData([]);
      setHaDataLoaded(false);
      setHaParams((prevParams) => ({
        ...prevParams,
        page: 1,
        validation_status: mergedFilters.selectedStatus?.map((status) => status?.value) || [],
        hazard_area_type: mergedFilters.selectedAreaType?.map((areaType) => areaType?.value) || [],
        donor: mergedFilters.selectedDonor?.map((donor) => donor?.value) || [],
        'task-customer': mergedFilters.selectedCustomer?.map((c) => c?.value) || [],
        team_id: mergedFilters.selectedTeam?.map((team) => team?.value) || [],
        local_community:
          mergedFilters.selectedLocalCommunity?.map((community) => community?.value) || [],
        hazard_name: mergedFilters.selectedPolygonName?.map((polygon) => polygon?.value) || [],
        hazard_type: mergedFilters.selectedHaMfBacType?.map((type) => type?.value) || [],
        square_min: mergedFilters.selectedSquare?.min || '',
        square_max: mergedFilters.selectedSquare?.max || '',
        imsma_id:
          mergedFilters.selectedImsmaId?.length > 0 ? mergedFilters.selectedImsmaId[0]?.value : '',
      }));
    }
  };

  const navigate = useNavigate();

  const handleSortIconClick = (ordering) => {
    if (ordering !== 'report_date' && !(ordering === 'area' && activeTabIndex === 1)) return;

    switch (activeTabIndex) {
      case 0:
        setNtsData([]);
        setNtsDataLoaded(false);
        setNtsParams((prevParams) => ({
          ...prevParams,
          page: 1,
          ordering: sortDirectionNts === true ? `-${ordering}` : ordering,
        }));
        setSortDirectionNts((prevDirection) => (prevDirection === true ? false : true));

        const ntsCurrentRotation = ntsRotations[ordering] || 0;
        const ntsNewRotation = ntsCurrentRotation + 180;

        if (ntsLastClickedIcon && ntsLastClickedIcon !== ordering) {
          setNtsRotations((prevRotations) => ({
            ...prevRotations,
            [ntsLastClickedIcon]: 0,
          }));
        }
        setNtsRotations((prevRotations) => ({
          ...prevRotations,
          [ordering]: ntsNewRotation,
        }));
        setNtsLastClickedIcon(ordering);
        break;
      case 1:
        setHaData([]);
        setHaDataLoaded(false);

        const haOrdering = ordering === 'area' ? 'report_area' : ordering;

        setHaParams((prevParams) => ({
          ...prevParams,
          page: 1,
          ordering: sortDirectionHa === true ? `-${haOrdering}` : haOrdering,
        }));
        setSortDirectionHa((prevDirection) => (prevDirection === true ? false : true));

        const haCurrentRotation = haRotations[ordering] || 0;
        const haNewRotation = haCurrentRotation + 180;

        if (haLastClickedIcon && haLastClickedIcon !== ordering) {
          setHaRotations((prevRotations) => ({
            ...prevRotations,
            [haLastClickedIcon]: 0,
          }));
        }
        setHaRotations((prevRotations) => ({
          ...prevRotations,
          [ordering]: haNewRotation,
        }));
        setHaLastClickedIcon(ordering);
        break;
      default:
        break;
    }
  };

  const handleNtsStartDateChange = (date) => {
    navigate(location.state, {});
    const formattedDate = date ? date.format('DD.MM.YYYY') : null;
    setNtsStartDate(formattedDate);
    setNtsData([]);
    setNtsDataLoaded(false);
    setNtsParams((prevParams) => ({
      ...prevParams,
      dateFrom: formattedDate,
      page: 1,
    }));
  };

  const handleNtsEndDateChange = (date) => {
    navigate(location.state, {});
    const formattedDate = date ? date.format('DD.MM.YYYY') : null;
    setNtsEndDate(formattedDate);
    setNtsData([]);
    setNtsDataLoaded(false);
    setNtsParams((prevParams) => ({
      ...prevParams,
      dateTo: formattedDate,
      page: 1,
    }));
  };

  const handleHaStartDateChange = (date) => {
    navigate(location.state, {});
    const formattedDate = date ? date.format('DD.MM.YYYY') : null;
    setHaStartDate(formattedDate);
    setHaData([]);
    setHaDataLoaded(false);
    setHaParams((prevParams) => ({
      ...prevParams,
      dateFrom: formattedDate,
      page: 1,
    }));
  };

  const handleHaEndDateChange = (date) => {
    navigate(location.state, {});
    const formattedDate = date ? date.format('DD.MM.YYYY') : null;
    setHaEndDate(formattedDate);
    setHaData([]);
    setHaDataLoaded(false);
    setHaParams((prevParams) => ({
      ...prevParams,
      dateTo: formattedDate,
      page: 1,
    }));
  };

  const handleTabClick = (index) => {
    setActiveTabIndex(index);
    setActiveFilter(null);
  };

  useEffect(() => {
    if (resetFiltersStatus) {
      const timer = setTimeout(() => {
        setResetFiltersStatus(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [resetFiltersStatus]);

  const confirmPopup = () => {
    const reportName = localStorage.getItem('reportName' || '');
    if (reportName) {
      navigate(URLS.CREATE_NTS_REPORT_PAGE, {
        state: { name: reportName },
      });
    }
  };

  const handleResetClick = () => {
    setResetFiltersStatus(true);

    clearAllHeaderFilters();

    if (activeTabIndex === 0) {
      setNtsRotations({ report_date: 0 });
      setNtsLastClickedIcon(null);
    } else {
      setHaRotations({ report_date: 0, area: 0 });
      setHaLastClickedIcon(null);
    }

    if (activeTabIndex === 0) {
      setNtsFilters({
        selectedFilters: [],
        selectedStatus: [],
        selectedDonor: [],
        selectedCustomer: [],
        selectedTeam: [],
        selectedLocalCommunity: [],
        selectedPolygonName: [],
        selectedType: [],
        selectedImsmaId: [],
      });

      setNtsData([]);
      setNtsStartDate(null);
      setNtsEndDate(null);
      setNtsDataLoaded(false);

      const resetParams = {
        validation_status: [],
        hazard_area_type: [],
        dateFrom: null,
        dateTo: null,
        page: 1,
        ordering: '',
        search: '',
        donor: [],
        'task-customer': [],
        team_id: [],
        local_community: [],
        hazard_name: [],
        imsma_id: [],
        type: [],
      };

      setNtsParams(resetParams);

      queueMicrotask(() => {
        fetchDataWithSortingAndFilter(
          resetParams,
          setNtsData,
          setIsNtsNextProperty,
          'nts/',
          null,
          null,
          teamLeadId,
          ntsAbortControllerRef,
          setNtsLoader,
          true
        );
        setNtsDataLoaded(true);
      });
    } else {
      setHaFilters({
        selectedFilters: [],
        selectedStatus: [],
        selectedDonor: [],
        selectedCustomer: [],
        selectedTeam: [],
        selectedLocalCommunity: [],
        selectedPolygonName: [],
        selectedType: [],
        selectedImsmaId: [],
        selectedAreaType: [],
        selectedHaMfBacType: [],
        selectedSquare: { min: '', max: '' },
      });

      setHaData([]);
      setHaStartDate(null);
      setHaEndDate(null);
      setHaDataLoaded(false);

      const resetParams = {
        dateFrom: null,
        dateTo: null,
        validation_status: [],
        hazard_area_type: [],
        page: 1,
        ordering: '',
        search: '',
        donor: [],
        'task-customer': [],
        team_id: [],
        local_community: [],
        hazard_name: [],
        hazard_type: [],
        square_min: '',
        square_max: '',
        imsma_id: [],
      };

      setHaParams(resetParams);

      queueMicrotask(() => {
        fetchDataWithSortingAndFilter(
          resetParams,
          setHaData,
          setIsHaNextProperty,
          'ha/',
          null,
          null,
          teamLeadId,
          haAbortControllerRef,
          setHaLoader,
          true
        );
        setHaDataLoaded(true);
      });
    }

    setTimeout(() => {
      setResetFiltersStatus(false);
    }, 100);

    navigate(location.state, {});
  };

  const deleteNtsReport = (selectedNtsReportId) => {
    if (activeTabIndex === 0) {
      setNtsLoader(true);
      axiosWrapper(() => {
        axios
          .delete(`/reports/nts/${selectedNtsReportId}/`)
          .then(() => {
            setNtsData((prevData) => prevData?.filter((item) => item?.id !== selectedNtsReportId));
            toast.success('NTS report deleted successfully');
          })
          .catch((err) => {
            console.error('Error deleting NTS report', err);
            toast.error('Failed to delete NTS report');
          })
          .finally(() => {
            setNtsLoader(false);
          });
      });
    }
  };

  const getEditReportUrl = (reportType, id) => {
    if (reportType === 'ha_report' || reportType === 'ha') {
      return `/edit-ha-report/${id}`;
    }
    if (reportType === 'nts_report') {
      return `/edit-nts-report/${id}`;
    }
    return '';
  };
  const [disabledDeleteDialogs, setDisabledDeleteDialogs] = useState({});
  const [disabledButtons, setDisabledButtons] = useState({});

  const handleAuxClick = (event, id) => {
    if (event.button === 1) {
      openIntoNewTab(id);
    }
  };

  const openIntoNewTab = (id) => {
    const mode = 'validation';
    localStorage.setItem('mode', mode);
    setDisabledButtons((prevState) => ({
      ...prevState,
      [id]: { validation: true, edit: true, delete: true },
    }));

    setDisabledDeleteDialogs((prevState) => ({
      ...prevState,
      [id]: true,
    }));
  };

  const EditReportLink = ({ reportType, id }) => {
    const url = getEditReportUrl(reportType, id);

    if (!url) return null;
    const isEditDisabled = disabledButtons[id]?.edit;

    return (
      <Link
        to={url}
        state={{ id }}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: isEditDisabled ? '#d3d3d3' : '#868686',
          pointerEvents: isEditDisabled ? 'none' : 'auto',
        }}
        onClick={() => openIntoNewTab(id)}
        onAuxClick={(event) => handleAuxClick(event, id)}
      >
        <EditIcon className="edit-icon" />
      </Link>
    );
  };

  const getViewReportUrl = (reportType, id) => {
    if (reportType === 'ha_report' || reportType === 'ha') {
      return `/view-ha-report/${id}`;
    }
    if (reportType === 'nts_report') {
      return `/view-nts-report/${id}`;
    }
    return '';
  };

  const ViewReportLink = ({ reportType, id }) => {
    const url = getViewReportUrl(reportType, id);
    if (!url) return null;

    return (
      <Link
        to={url}
        state={{ id }}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#868686' }}
      >
        <ViewIcon className="view-icon" />
      </Link>
    );
  };

  const getValidationReportUrl = (reportType, id) => {
    if (reportType === 'ha_report' || reportType === 'ha') {
      return `/validation-ha-report/${id}`;
    }
    if (reportType === 'nts_report') {
      return `/validation-nts-report/${id}`;
    }
    return '';
  };

  const ValidationReportLink = ({ reportType, id }) => {
    const url = getValidationReportUrl(reportType, id);
    if (!url) return null;
    const isValidationDisabled = disabledButtons[id]?.validation;

    return (
      <Link
        to={url}
        target="_blank"
        rel="noopener noreferrer"
        className="view-icon"
        style={{
          color: isValidationDisabled ? '#d3d3d3' : '#868686',
          pointerEvents: isValidationDisabled ? 'none' : 'auto',
        }}
        onClick={() => openIntoNewTab(id)}
        onAuxClick={(event) => handleAuxClick(event, id)}
      >
        <CommentIcon className="view-icon" />
      </Link>
    );
  };

  const deleteHaReport = (selectedHaReportId) => {
    if (activeTabIndex === 1) {
      setHaLoader(true);
      axiosWrapper(() => {
        axios
          .delete(`/reports/ha/${selectedHaReportId}/`)
          .then(() => {
            setHaData((prevData) => prevData?.filter((item) => item?.id !== selectedHaReportId));
            toast.success('HA report deleted successfully');
          })
          .catch((err) => {
            console.error('Error deleting HA report', err);
            toast.error('Failed to delete HA report');
          })
          .finally(() => {
            setHaLoader(false);
          });
      });
    }
  };

  const getActionsByRoleAndStatus = (item) => {
    const draftOrRejectedStatus =
      item?.validation_status?.value === 'draft' ||
      item?.validation_status?.value === 'draft_rejected';
    const validationStatus =
      item?.validation_status?.value?.startsWith('val_') ||
      item?.validation_status?.value === 'imsma_rejected';
    return (
      <div onContextMenu={handleContextMenu}>
        {user?.permissions?.change_nto_report && draftOrRejectedStatus && (
          <EditReportLink reportType={item?.report_type} id={item?.id} />
        )}
        {user?.permissions?.view_nto_report && (
          <ViewReportLink reportType={item?.report_type} id={item?.id} />
        )}
        {user?.permissions?.delete_nto_report && draftOrRejectedStatus && (
          <DeleteReportDialog
            deleteNtsReport={activeTabIndex === 0 ? () => deleteNtsReport(item?.id) : null}
            deleteHaReport={activeTabIndex === 1 ? () => deleteHaReport(item?.id) : null}
            isDisabled={disabledDeleteDialogs[item?.id]}
          />
        )}
        {user?.permissions?.validate_nto_report &&
          item?.is_current_validation_role &&
          validationStatus && <ValidationReportLink reportType={item?.report_type} id={item?.id} />}
      </div>
    );
  };

  useEffect(() => {
    document.title = 'NTS and HA reports';
    if (location?.state?.reportType === 'ha') {
      setActiveTabIndex(1);
    }
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    }

    if (activeTabIndex === 0 && !ntsDataLoaded) {
      fetchDataWithSortingAndFilter(
        ntsParams,
        setNtsData,
        setIsNtsNextProperty,
        'nts/',
        ntsParams?.dateFrom,
        ntsParams?.dateTo,
        teamLeadId,
        ntsAbortControllerRef,
        setNtsLoader,
        true
      );
      setNtsDataLoaded(true);
    } else if (activeTabIndex === 1 && !haDataLoaded) {
      fetchDataWithSortingAndFilter(
        haParams,
        setHaData,
        setIsHaNextProperty,
        'ha/',
        haParams?.dateFrom,
        haParams?.dateTo,
        teamLeadId,
        haAbortControllerRef,
        setHaLoader,
        true
      );
      setHaDataLoaded(true);
    }
  }, [activeTabIndex]);

  useEffect(() => {
    return () => {
      if (ntsAbortControllerRef.current) {
        ntsAbortControllerRef.current.abort();
      }
      if (haAbortControllerRef.current) {
        haAbortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <section>
      <div className="nts-header">
        <div className="nts-title">NTS / HA report</div>
        <div
          style={{
            display: 'flex',
            gap: '5px',
          }}
        >
          {activeTabIndex === 0 ? (
            <>
              <div className="flex-group flex-between" style={{ width: '150px' }}>
                <DatePickerWrapper
                  value={ntsStartDate ? moment(ntsStartDate, 'DD.MM.YYYY') : null}
                  handleDatePicker={handleNtsStartDateChange}
                />
              </div>
              <div
                className="flex-group flex-between"
                style={{ width: '150px', paddingRight: '10px' }}
              >
                <DatePickerWrapper
                  value={ntsEndDate ? moment(ntsEndDate, 'DD.MM.YYYY') : null}
                  handleDatePicker={handleNtsEndDateChange}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex-group flex-between" style={{ width: '150px' }}>
                <DatePickerWrapper
                  value={haStartDate ? moment(haStartDate, 'DD.MM.YYYY') : null}
                  handleDatePicker={handleHaStartDateChange}
                />
              </div>
              <div
                className="flex-group flex-between"
                style={{ width: '150px', paddingRight: '10px' }}
              >
                <DatePickerWrapper
                  value={haEndDate ? moment(haEndDate, 'DD.MM.YYYY') : null}
                  handleDatePicker={handleHaEndDateChange}
                />
              </div>
            </>
          )}
          <div className="text-hover p-1" onClick={handleResetClick}>
            <ResetIcon color="#868686" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '50px' }}>
          <div style={{ display: 'flex', gap: '14px', marginRight: '100px' }}>
            {
              <FilterByStatusDialog
                key={`filter-dialog-${activeTabIndex}`}
                filterType={activeTabIndex === 0 ? 'nts' : 'ha'}
                activeTabIndex={activeTabIndex}
                onFiltersChange={handleNtsHaFiltersChange}
                handleSelectedStatusChange={handleSelectedStatusChange}
                resetFiltersStatus={resetFiltersStatus}
                getCurrentCompleteFilters={getCurrentCompleteFilters}
                currentDates={{
                  dateFrom: activeTabIndex === 0 ? ntsStartDate : haStartDate,
                  dateTo: activeTabIndex === 0 ? ntsEndDate : haEndDate,
                }}
              />
            }
          </div>

          <div style={{ marginTop: '10px' }}>
            <Tab>
              <div
                className={`tab-item-custom ${activeTabIndex === 0 ? 'active' : ''}`}
                onClick={() => handleTabClick(0)}
              >
                NTS
              </div>
              <div
                className={`tab-item-custom ${activeTabIndex === 1 ? 'active' : ''}`}
                onClick={() => handleTabClick(1)}
              >
                HA
              </div>
            </Tab>
          </div>
        </div>
      </div>
      <div className="nts-table">
        <div className="eore-table" style={{ position: 'relative' }}>
          <Table>
            <TableHeader>
              {(activeTabIndex === 0 ? NTS_HEADERS : HA_HEADERS)?.map((header, index) => (
                <th
                  key={index}
                  className={`${header.key && header.condition && header.condition() ? 'text-with-icon' : ''} ${isFilterableHeader(header.key) ? 'filterable-header' : ''}`}
                  style={{
                    ...header.style,
                    cursor: isFilterableHeader(header?.key) ? 'pointer' : 'default',
                    position: 'relative',
                  }}
                  onClick={(event) => handleHeaderClick(event, header.key)}
                >
                  {header?.key && header?.condition && header?.condition() && (
                    <SortByIcon
                      className="sort-icon"
                      style={{
                        transform: `rotate(${(activeTabIndex === 0 ? ntsRotations : haRotations)[header.key] || 0}deg)`,
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSortIconClick(header.key);
                      }}
                    />
                  )}
                  {header.label}
                  {isFilterableHeader(header?.key) && getActiveFiltersCount(header?.key) > 0 && (
                    <span className="filter-badge">{getActiveFiltersCount(header?.key)}</span>
                  )}
                </th>
              ))}
            </TableHeader>
            <tbody>
              {activeTabIndex === 0 &&
                (ntsData && ntsData?.length === 0 ? (
                  ntsLoader ? (
                    <TableRow>
                      <td colSpan="8" style={{ textAlign: 'center' }}>
                        Loading...
                      </td>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <td colSpan="8">Дані відсутні</td>
                    </TableRow>
                  )
                ) : (
                  <ReportsList
                    data={ntsData}
                    setData={setNtsData}
                    activeTabIndex={activeTabIndex}
                    formatDate={formatDate}
                    getActionsByRoleAndStatus={getActionsByRoleAndStatus}
                  />
                ))}

              {activeTabIndex === 1 &&
                (haData && haData?.length === 0 ? (
                  haLoader ? (
                    <TableRow>
                      <td colSpan="10" style={{ textAlign: 'center' }}>
                        Loading...
                      </td>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <td colSpan="10">Дані відсутні</td>
                    </TableRow>
                  )
                ) : (
                  <ReportsList
                    data={haData}
                    setData={setHaData}
                    activeTabIndex={activeTabIndex}
                    formatDate={formatDate}
                    getActionsByRoleAndStatus={getActionsByRoleAndStatus}
                  />
                ))}
            </tbody>
          </Table>

          {activeFilter &&
            (activeFilter === 'imsma_id' ? (
              <ImsmaIdFilterDropdown
                key={activeFilter}
                onClose={closeFilter}
                title={
                  (activeTabIndex === 0 ? NTS_HEADERS : HA_HEADERS).find(
                    (h) => h.key === activeFilter
                  )?.label || activeFilter
                }
                selectedValues={getCurrentHeaderFilters()[activeFilter] || []}
                onSelectionChange={(selectedValues) =>
                  handleHeaderFilterChange(activeFilter, selectedValues)
                }
                position={filterPosition}
              />
            ) : (
              <FilterDropdown
                key={activeFilter}
                onClose={closeFilter}
                title={
                  (activeTabIndex === 0 ? NTS_HEADERS : HA_HEADERS).find(
                    (h) => h.key === activeFilter
                  )?.label || activeFilter
                }
                selectedValues={getCurrentHeaderFilters()[activeFilter] || []}
                onSelectionChange={(selectedValues) =>
                  handleHeaderFilterChange(activeFilter, selectedValues)
                }
                position={filterPosition}
                reportType={activeTabIndex === 0 ? 'nts' : 'ha'}
                filterType={activeFilter}
                currentFilters={getCurrentCompleteFilters()}
                currentDates={{
                  startDate: activeTabIndex === 0 ? ntsStartDate : haStartDate,
                  endDate: activeTabIndex === 0 ? ntsEndDate : haEndDate,
                }}
              />
            ))}
        </div>
        <div className="loadMore-div">
          {activeTabIndex === 1 && isHaNextProperty !== null && (
            <button
              className="loadMore-button"
              onClick={() => {
                loadMoreData('ha');
              }}
            >
              Load more...
            </button>
          )}
        </div>
        <div className="loadMore-div">
          {activeTabIndex === 0 && isNtsNextProperty !== null && (
            <button
              className="loadMore-button"
              onClick={() => {
                loadMoreData('nts');
              }}
            >
              Load more...
            </button>
          )}
        </div>
        {user?.permissions?.add_nto_report && (
          <>
            <hr className="nts-line" />
            <div className="nts-footer">
              <CreateReportDialog confirmPopup={confirmPopup} />
            </div>
          </>
        )}
      </div>
      <Loader visible={ntsLoader || haLoader} />
      <ToastContainer />
    </section>
  );
};
